import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem
} from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  VpnKey as VpnKeyIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import { useUsuarioStore } from '../store/usuarioStore';
import { usuarioService } from '../services/usuarioService';
import type { Usuario } from '../types/usuario.types';
import UsuarioDialog from './UsuarioDialog';
import AsignarRolesDialog from './AsignarRolesDialog';
import CambiarPasswordDialog from './CambiarPasswordDialog';

export const UsuariosPage = () => {
  const { usuarios, setUsuarios, setLoading, removeUsuario } = useUsuarioStore();
  const [openDialog, setOpenDialog] = useState(false);
  const [openRolesDialog, setOpenRolesDialog] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState<Usuario | undefined>();

  useEffect(() => {
    loadUsuarios();
  }, []);

  const loadUsuarios = async () => {
    setLoading(true);
    try {
      const data = await usuarioService.getAll();
      setUsuarios(data);
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setSelectedUsuario(undefined);
    setOpenDialog(true);
  };

  const handleEdit = (usuario: Usuario) => {
    setSelectedUsuario(usuario);
    setOpenDialog(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Está seguro de eliminar este usuario?')) {
      try {
        await usuarioService.delete(id);
        removeUsuario(id);
      } catch (error) {
        console.error('Error al eliminar usuario:', error);
      }
    }
  };

  const handleToggleBloqueo = async (usuario: Usuario) => {
    try {
      await usuarioService.bloquear(usuario.id, !usuario.isLocked);
      loadUsuarios();
    } catch (error) {
      console.error('Error al cambiar bloqueo:', error);
    }
  };

  const handleAsignarRoles = (usuario: Usuario) => {
    setSelectedUsuario(usuario);
    setOpenRolesDialog(true);
  };

  const handleCambiarPassword = (usuario: Usuario) => {
    setSelectedUsuario(usuario);
    setOpenPasswordDialog(true);
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'userName', headerName: 'Usuario', flex: 1 },
    {
      field: 'persona',
      headerName: 'Nombre Completo',
      flex: 1.5,
      valueGetter: (_value, row) => {
        const persona = row.persona;
        if (!persona) return 'Sin persona';
        return persona.personaContacto || persona.identificacion || '';
      }
    },
    {
      field: 'isActive',
      headerName: 'Estado',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Activo' : 'Inactivo'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'isLocked',
      headerName: 'Bloqueado',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Bloqueado' : 'Activo'}
          color={params.value ? 'error' : 'success'}
          size="small"
        />
      )
    },
    {
      field: 'roles',
      headerName: 'Roles',
      width: 80,
      renderCell: (params) => (
        <Chip
          label={params.value?.length || 0}
          color="primary"
          size="small"
        />
      )
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: 200,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Editar"
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={params.row.isLocked ? <LockOpenIcon /> : <LockIcon />}
          label={params.row.isLocked ? 'Desbloquear' : 'Bloquear'}
          onClick={() => handleToggleBloqueo(params.row)}
        />,
        <GridActionsCellItem
          icon={<ShieldIcon />}
          label="Roles"
          onClick={() => handleAsignarRoles(params.row)}
        />,
        <GridActionsCellItem
          icon={<VpnKeyIcon />}
          label="Cambiar Password"
          onClick={() => handleCambiarPassword(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Eliminar"
          onClick={() => handleDelete(params.row.id)}
        />
      ]
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5">Gestión de Usuarios</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNew}
          >
            Nuevo Usuario
          </Button>
        </Box>

        <DataGrid
          rows={usuarios}
          columns={columns}
          autoHeight
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 }
            }
          }}
          pageSizeOptions={[5, 10, 25]}
          disableRowSelectionOnClick
        />
      </Paper>

      <UsuarioDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        usuario={selectedUsuario}
        onSuccess={loadUsuarios}
      />

      <AsignarRolesDialog
        open={openRolesDialog}
        onClose={() => setOpenRolesDialog(false)}
        usuario={selectedUsuario}
        onSuccess={loadUsuarios}
      />

      <CambiarPasswordDialog
        open={openPasswordDialog}
        onClose={() => setOpenPasswordDialog(false)}
        usuario={selectedUsuario}
      />
    </Box>
  );
};

export default UsuariosPage;
